import { useCallback, useEffect } from 'react';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import type { FilterOption } from '~/modules/settings/types/filter';

interface UseFilterDrawerProps<T extends FieldValues> {
  open: boolean;
  onApply: () => void;
  filterForm: {
    getValues: UseFormReturn<T>['getValues'];
    setValue: UseFormReturn<T>['setValue'];
    reset: UseFormReturn<T>['reset'];
  };
}

export const useFilterDrawer = <T extends FieldValues>({
  open,
  onApply,
  filterForm,
}: UseFilterDrawerProps<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isFilterKey = useCallback(
    (key: string): key is Path<T> => {
      const defaultValues = filterForm.getValues ? filterForm.getValues() : {};

      return key in defaultValues;
    },
    [filterForm],
  );

  useEffect(() => {
    if (open) {
      for (const [key, value] of searchParams) {
        if (isFilterKey(key) && filterForm.setValue) {
          const formValue = filterForm.getValues(key);

          if (Array.isArray(formValue)) {
            filterForm.setValue(
              key,
              value.split(',').map((val) => ({ value: val })) as PathValue<
                T,
                Path<T>
              >,
            );
          } else {
            filterForm.setValue(key, value as PathValue<T, Path<T>>);
          }
        }
      }
    }
  }, [filterForm, open, searchParams, isFilterKey]);

  const handleClear = useCallback(() => {
    filterForm.reset();
  }, [filterForm]);

  const handleApply = useCallback(
    (filterData: T) => {
      for (const [key, filter] of Object.entries(
        filterData as Record<string, string | FilterOption[]>,
      )) {
        if (Array.isArray(filter) && filter.length) {
          searchParams.set(
            key,
            filter.map((item: { value: string }) => item.value).join(','),
          );
        } else if (filter && !Array.isArray(filter) && filter !== 'all') {
          searchParams.set(key, filter);
        } else if (
          (filter === 'all' && searchParams.has(key)) ||
          (Array.isArray(filter) && filter.length === 0)
        ) {
          searchParams.delete(key);
        }
      }

      setSearchParams(searchParams);
      onApply();
    },
    [onApply, searchParams, setSearchParams],
  );

  return {
    handleClear,
    handleApply,
  };
};
